import { useContext, useEffect, useState } from "react";

import { Actions, AppContext } from "@Context/common/general/context";

import { normalizeSessionResults } from "./session-results.utils";
import { type RawSessionResultsData, type SessionResultsData } from "./use-session-results.types";

const INCOMPLETE_RESULTS_RETRY_LIMIT = 3;
const INCOMPLETE_RESULTS_RETRY_DELAY_MS = 500;

const hasIncompleteScenarioResults = (response: RawSessionResultsData) => {
  const expectedScenarioCount = response.play_items?.length ?? 0;
  const resultCount = response.play_item_results?.length ?? 0;

  return expectedScenarioCount > resultCount;
};

export const useSessionResultsRequest = (sessionId: string | undefined) => {
  const { dispatch } = useContext(AppContext);
  const [sessionResults, setSessionResults] = useState<SessionResultsData | null>(null);

  useEffect(() => {
    if (!sessionId) {
      return;
    }

    let isActive = true;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;

    const requestSessionResults = (retryCount = 0) => {
      dispatch({
        type: Actions.SessionActions.GetSessionResults,
        disableLoaders: true,
        payload: {
          play_session_id: +sessionId,
          resolve_previous_session: true,
        },
        onSuccess: response => {
          if (!isActive) {
            return;
          }

          const rawResults = response as RawSessionResultsData;
          setSessionResults(normalizeSessionResults(rawResults));

          if (hasIncompleteScenarioResults(rawResults) && retryCount < INCOMPLETE_RESULTS_RETRY_LIMIT) {
            retryTimeout = setTimeout(() => requestSessionResults(retryCount + 1), INCOMPLETE_RESULTS_RETRY_DELAY_MS * (retryCount + 1));
          }
        },
      });
    };

    requestSessionResults();

    return () => {
      isActive = false;
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
    // `dispatch` from AppContext is not guaranteed stable; avoid request loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return sessionResults;
};
